import { useRef, useState } from "react";
import * as Papa from "papaparse";

import { CsvParseError, RowLimitExceededError } from "@/lib/csv-import/csv-parse-error";
import { readBoundedXlsxArchive, XlsxArchiveLimits } from "@/lib/csv-import/read-bounded-xlsx-archive";

interface CsvState {
  fileName: string;
  data: {
    parsed: Record<string, unknown>[];
    mapped: Record<string, unknown>[];
  };
  fieldMappings: {
    original: Record<string, string | undefined>;
    current: Record<string, string | undefined>;
  };
  error: CsvParseError | null;
}

interface ParseCsvOptions extends Papa.ParseConfig {
  allowXlsx?: boolean;
  xlsxLimits?: XlsxArchiveLimits;
  /**
   * Array of field mappings defining the structure of the imported data.
   * Each field includes a label, value, and optional required flag.
   * @example fields={[{ label: 'Name', value: 'name', required: true }, { label: 'Email', value: 'email' }]}
   */
  fields: { label: string; value: string; required?: boolean }[];

  onError?: (error: CsvParseError) => void;

  /**
   * Flag to indicate if empty fields should be shown.
   * @default false
   * @example showEmptyFields={true}
   */
  showEmptyFields?: boolean;
}

type UseParseCsvProps = ParseCsvOptions &
  (
    | { strict: true; onSuccess?: (data: Record<string, unknown>[], fileRowList: number[]) => void }
    | { strict?: false; onSuccess?: (data: Record<string, unknown>[]) => void }
    | { strict: boolean; onSuccess?: never }
  );

export function useParseCsv({
  fields,
  onSuccess,
  onError,
  showEmptyFields,
  strict,
  allowXlsx = false,
  xlsxLimits,
  ...props
}: UseParseCsvProps) {
  const parseRequest = useRef(0);
  const [isParsing, setIsParsing] = useState(false);
  const [csvState, setCsvState] = useState<CsvState>({
    fileName: "",
    data: {
      parsed: [],
      mapped: [],
    },
    fieldMappings: {
      current: {},
      original: {},
    },
    error: null,
  });

  function onParse({ file, limit = Infinity }: { file: File; limit?: number }) {
    const request = ++parseRequest.current;
    setIsParsing(true);
    setCsvState((state) => ({
      ...state,
      error: null,
      data: { parsed: [], mapped: [] },
      fieldMappings: { original: {}, current: {} },
    }));
    if (allowXlsx && file.name.toLowerCase().endsWith(".xlsx")) {
      void import("read-excel-file/browser")
        .then(async ({ readSheet }) => {
          const archive = await readBoundedXlsxArchive({ file, limit, limits: xlsxLimits });
          if (request !== parseRequest.current) return;
          const rowList = await readSheet(archive, 1);
          if (request !== parseRequest.current) return;
          const width = rowList[0]?.length ?? 0;
          const csvRowList = rowList.map((row) =>
            Array.from({ length: Math.max(width, row.length) }, (_, index) => {
              const value = row[index];
              return typeof value === "number" ? String(value).replace(".", ",") : String(value ?? "");
            }),
          );
          const csvFile = new File([Papa.unparse(csvRowList)], file.name.replace(/\.xlsx$/i, ".csv"), {
            type: "text/csv",
          });
          if (strict) {
            parseStrict({ file: csvFile, limit, rowNumbering: "record" });
          } else {
            onParse({ file: csvFile, limit });
          }
        })
        .catch((error: unknown) => {
          if (request !== parseRequest.current) return;
          setIsParsing(false);
          const message: CsvParseError = error instanceof RowLimitExceededError ? "rowLimitExceeded" : "invalid";
          setCsvState((state) => ({ ...state, error: message }));
          onError?.(message);
        });
      return;
    }
    function parseStrict({
      file,
      limit,
      rowNumbering,
    }: {
      file: File;
      limit: number;
      rowNumbering: "record" | "line";
    }): void {
      let hasDelimitedHeader = false;
      const parsedRowList: string[][] = [];
      const parsedFileRowList: number[] = [];
      let fileRow = 1;
      const parseErrorList: Papa.ParseError[] = [];
      Papa.parse<string[]>(file, {
        header: false,
        dynamicTyping: false,
        delimiter: (chunk) => {
          const result = Papa.parse<string[]>(chunk, { preview: 1, skipEmptyLines: "greedy" });
          parseErrorList.push(...result.errors);
          return result.meta.delimiter;
        },
        beforeFirstChunk: (chunk) => {
          const delimiterList = [",", "\t", "|", ";", Papa.RECORD_SEP, Papa.UNIT_SEP];
          hasDelimitedHeader = delimiterList.some((delimiter) => {
            const result = Papa.parse<string[]>(chunk, { delimiter, preview: 1, skipEmptyLines: "greedy" });
            return (result.data[0]?.length ?? 0) > 1;
          });
          return chunk;
        },
        step: (result, parser) => {
          if (request !== parseRequest.current) {
            parser.abort();
            return;
          }
          parseErrorList.push(...result.errors);
          const currentFileRow = fileRow;
          fileRow +=
            rowNumbering === "record"
              ? 1
              : 1 + result.data.reduce((count, value) => count + value.split(/\r\n|\r|\n/).length - 1, 0);
          if (result.data.join("").trim() === "") return;
          parsedFileRowList.push(currentFileRow);
          parsedRowList.push(result.data);
          if (parsedRowList.length > limit + 1) parser.abort();
        },
        complete: () => {
          if (request !== parseRequest.current) return;
          const [header = [], ...rowList] = parsedRowList;
          const columnList = header.map((column, index) => column.trim() || `Field ${index + 1}`);
          const hasInvalidFormat =
            parseErrorList.some((error) => error.code !== "UndetectableDelimiter" || hasDelimitedHeader) ||
            new Set(columnList.map((column) => column.toLowerCase())).size !== columnList.length ||
            rowList.some((row) => row.length !== columnList.length);
          const error: CsvParseError | null =
            rowList.length > limit
              ? "rowLimitExceeded"
              : hasInvalidFormat
                ? "invalid"
                : !rowList.length
                  ? "empty"
                  : null;
          setIsParsing(false);
          const parsed = error
            ? []
            : rowList.map((row) => Object.fromEntries(columnList.map((column, index) => [column, row[index]])));
          const mappings = Object.fromEntries(columnList.map((column) => [column, column]));
          setCsvState({
            fileName: file.name,
            data: { parsed, mapped: parsed },
            fieldMappings: { original: mappings, current: mappings },
            error,
          });
          if (error) {
            onError?.(error);
          } else {
            onSuccess?.(parsed, parsedFileRowList.slice(1));
          }
        },
        error: () => {
          if (request !== parseRequest.current) return;
          setIsParsing(false);
          setCsvState((state) => ({ ...state, error: "invalid" }));
          onError?.("invalid");
        },
      });
    }
    if (strict) {
      parseStrict({ file, limit, rowNumbering: "line" });
      return;
    }
    let count = 0;
    let hasExceededRowLimit = false;
    const allResults: Record<string, unknown>[] = [];

    Papa.parse<Record<string, unknown>>(file, {
      ...props,
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      beforeFirstChunk: (chunk) => {
        const parsedChunk = Papa.parse<string[]>(chunk, {
          header: false,
          skipEmptyLines: true,
        });

        const rows = parsedChunk.data;
        const columns = rows[0] ?? [];

        const newColumns = columns
          .map((column, index) => {
            if (column.trim() === "" && !showEmptyFields) {
              const hasNonEmptyValue = rows
                .slice(1)
                .some((row) => row[index] !== "" && row[index] !== null && row[index] !== undefined);
              if (!hasNonEmptyValue) {
                return null;
              }
            }
            return column.trim() === "" ? `Field ${index + 1}` : column;
          })
          .filter((column) => column !== null);

        rows[0] = newColumns as string[];
        return Papa.unparse(rows);
      },
      step: (results, parser) => {
        if (request !== parseRequest.current) {
          parser.abort();
          return;
        }
        if (count === 0) {
          const mappings = (results.meta.fields ?? [])?.reduce(
            (acc, field) => ({
              ...acc,
              [field]: field,
            }),
            {},
          );

          setCsvState((prevState) => ({
            ...prevState,
            fieldMappings: {
              original: mappings,
              current: mappings,
            },
          }));
        }

        if (count < limit) {
          allResults.push(results.data);
          count++;
        } else {
          hasExceededRowLimit = true;
          setIsParsing(false);
          setCsvState((state) => ({ ...state, error: "rowLimitExceeded" }));
          onError?.("rowLimitExceeded");
          parser.abort();
        }
      },
      complete: (_, localFile: File) => {
        if (request !== parseRequest.current || hasExceededRowLimit) return;
        setIsParsing(false);
        setCsvState((prevState) => ({
          ...prevState,
          fileName: localFile?.name ? localFile.name.replace(/\.[^/.]+$/, "") : "Untitled",
          data: {
            parsed: allResults,
            mapped: allResults,
          },
        }));
        if (!strict) onSuccess?.(allResults);
      },
      error: () => {
        if (request !== parseRequest.current) return;
        setIsParsing(false);
        setCsvState((state) => ({ ...state, error: "invalid" }));
        onError?.("invalid");
      },
    });
  }

  function onFieldChange({ oldValue, newValue }: { oldValue: string; newValue: string }) {
    setCsvState((prevState) => ({
      ...prevState,
      fieldMappings: {
        ...prevState.fieldMappings,
        current: { ...prevState.fieldMappings.current, [newValue]: oldValue },
      },
      data: {
        ...prevState.data,
        mapped: prevState.data.mapped.map((row, index) => ({
          ...row,
          [newValue]: prevState.data.parsed[index]?.[oldValue],
        })),
      },
    }));
  }

  function onFieldToggle({ value, checked }: { value: string; checked: boolean }) {
    setCsvState((prevState) => ({
      ...prevState,
      fieldMappings: {
        ...prevState.fieldMappings,
        current: {
          ...prevState.fieldMappings.current,
          [value]: checked ? "" : undefined,
        },
      },
      data: {
        ...prevState.data,
        mapped: prevState.data.mapped.map((row) => {
          const { [value]: _, ...rest } = row;
          return rest;
        }),
      },
    }));
  }

  function onFieldsReset() {
    setCsvState((prevState) => {
      const current = { ...prevState.fieldMappings.original };
      if (strict) {
        fields.forEach((field) => {
          const source = Object.values(prevState.fieldMappings.original)
            .reverse()
            .find((column) => column?.toLowerCase() === field.label.toLowerCase());
          if (source) current[field.value] = source;
        });
      }
      const mapped = prevState.data.parsed.map((row) => ({
        ...row,
        ...Object.fromEntries(Object.entries(current).map(([target, source]) => [target, source ? row[source] : ""])),
      }));
      return {
        ...prevState,
        fieldMappings: { ...prevState.fieldMappings, current },
        data: { ...prevState.data, mapped },
      };
    });
  }

  function onCellChange(rowIndex: number, fieldValue: string, newValue: unknown) {
    setCsvState((prevState) => ({
      ...prevState,
      data: {
        ...prevState.data,
        mapped: prevState.data.mapped.map((row, i) => (i === rowIndex ? { ...row, [fieldValue]: newValue } : row)),
      },
    }));
  }

  function getSanitizedData({ data }: { data: Record<string, unknown>[] }) {
    return data.map((row) =>
      Object.keys(row).reduce(
        (acc, key) => ({
          ...acc,
          [key]: row[key] === null ? "" : row[key],
        }),
        {},
      ),
    );
  }

  return {
    isParsing,
    fileName: csvState.fileName,
    data: csvState.data.mapped,
    fieldMappings: csvState.fieldMappings,
    error: csvState.error,
    getSanitizedData,
    onParse,
    onFieldChange,
    onFieldToggle,
    onFieldsReset,
    onCellChange,
  };
}
